import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  QuickstartTheme,
  getLoadingMessage,
  isSeamlessMode,
  getResponsiveHeight,
  getResolvedCtaUrl,
} from '../types/theme';
import { QuickstartPhase } from '../QuickstartCoordinator';
import { LoadingBar } from './LoadingBar';
import { ThemeLogo } from './ThemeLogo';
import { useIsLandscape, useIsPortraitOrientation } from '../utils/responsiveHooks';
import { useIsMobileDevice } from '../../../utility/utility';
import { ScreenEdgeBorder } from './ScreenEdgeBorder';

/**
 * Loading state determines what to show in the overlay.
 * 'initial' shows loading bar + message, 'takingLong' adds a CTA button.
 */
type LoadingState = 'initial' | 'connecting' | 'takingLong';

/**
 * Get loading state from quickstart phase.
 * All pre-play phases return 'initial' so the loading bar persists
 * until the player component takes over.
 */
function getLoadingStateFromPhase(phase: QuickstartPhase): LoadingState {
  switch (phase) {
    case 'page_viewed':
    case 'environment_check':
    case 'creating_lobby':
    case 'matchmaking':
      return 'initial';

    case 'match_found':
    case 'joining_broadcast':
    case 'preparing_player':
      return 'connecting';

    default:
      return 'initial';
  }
}

interface SeamlessLoadingOverlayProps {
  theme: QuickstartTheme;
  phase: QuickstartPhase;
  urlSlug: string;
  /** Time in ms before showing "taking long" message (default: 8000 = 8 seconds) */
  takingLongTimeout?: number;
  /** Whether to show the "Tap Here To Join" button when ready */
  showJoinButton?: boolean;
  /** Callback when join button is clicked */
  onJoinClick?: () => void;
  /** Callback when wishlist button is clicked (shown when taking too long) */
  onWishlistClick?: () => void;
  /** Callback when loading times out (auto-redirect trigger) */
  onLoadingTimeout?: () => void;
}

/**
 * SeamlessLoadingOverlay Component
 *
 * A transparent overlay for seamless_loading_screen mode that shows
 * loading messages and status on top of the video background.
 *
 * Features:
 * - Displays context-aware loading messages
 * - Shows "taking longer than expected" after timeout
 * - Animated text transitions
 * - Badge/logo display from theme
 * - Works with transparent background (video shows through)
 *
 * Usage:
 * ```tsx
 * {isSeamlessMode(theme) && (
 *   <SeamlessLoadingOverlay theme={theme} phase={phase} />
 * )}
 * ```
 */
export const SeamlessLoadingOverlay = ({
  theme,
  phase,
  urlSlug,
  takingLongTimeout = 15000, // 15 seconds before showing "taking long" message
  showJoinButton = false,
  onJoinClick,
  onWishlistClick,
  onLoadingTimeout,
}: SeamlessLoadingOverlayProps) => {
  const [loadingState, setLoadingState] = useState<LoadingState>('initial');
  const [showTakingLong, setShowTakingLong] = useState(false);

  // Detect landscape orientation (forced on mobile)
  const isLandscape = useIsLandscape();

  // Detect actual physical orientation for rotation
  const isPortraitOrientation = useIsPortraitOrientation();

  const isMobile = useIsMobileDevice();

  // Track loading state from phase
  useEffect(() => {
    const stateFromPhase = getLoadingStateFromPhase(phase);
    setLoadingState(stateFromPhase);
  }, [phase]);

  // Set timeout for "taking long" message or auto-redirect
  useEffect(() => {
    // Only trigger during initial loading phases
    if (loadingState !== 'initial') {
      return;
    }

    // Check if auto-redirect is enabled in theme AND a CTA URL is available
    const hasCtaUrl = !!getResolvedCtaUrl(theme);
    if (theme.autoRedirectToCTA && hasCtaUrl && onLoadingTimeout) {
      // If auto-redirect is enabled, trigger redirect after timeout instead of showing message
      console.log('[SeamlessLoadingOverlay] Auto-redirect enabled, will trigger redirect after', takingLongTimeout, 'ms');
      const timer = setTimeout(() => {
        console.log('[SeamlessLoadingOverlay] Loading timeout reached - triggering auto-redirect');
        onLoadingTimeout();
      }, takingLongTimeout);

      return () => clearTimeout(timer);
    }

    // If auto-redirect is NOT enabled (or no CTA URL), show the button (existing behavior)
    const timer = setTimeout(() => {
      setShowTakingLong(true);
    }, takingLongTimeout);

    return () => clearTimeout(timer);
  }, [loadingState, takingLongTimeout, theme, onLoadingTimeout]);

  // Get the appropriate message
  const getMessage = (): string => {
    if (showTakingLong) {
      return getLoadingMessage(theme, 'takingLong');
    }
    return getLoadingMessage(theme, 'initial');
  };

  const message = getMessage();

  // Don't render if not in seamless mode
  if (!isSeamlessMode(theme)) {
    return null;
  }

  // Apply rotation on mobile devices in portrait orientation
  const shouldRotate = isLandscape && isPortraitOrientation;

  return (
    <>
      <div
        className='pointer-events-none fixed z-10 flex flex-col items-center justify-center'
        style={
          shouldRotate ? {
            // Portrait mode: swap dimensions and rotate
            position: 'fixed',
            left: '50%',
            top: '50%',
            width: '100dvh',
            height: '100dvw',
            transform: 'translate(-50%, -50%) rotate(var(--force-rotate-angle, 90deg))',
            transformOrigin: 'center center',
          } : {
            // Normal mode: full screen
            inset: 0,
          }
        }
      >
      <div
        className='flex w-full flex-col items-center'
        style={shouldRotate ? { padding: '0 1rem' } : undefined}
      >
        {/* Content Container - Fixed width for consistency, smaller on mobile */}
        <div
          className='pointer-events-auto flex flex-col items-center text-center'
          style={{
            width: shouldRotate || window.innerWidth >= 640 ? '420px' : '100%',
            maxWidth: '100%',
          }}
        >
        {/* Studio Logo */}
        <ThemeLogo
          kind='studio'
          theme={theme}
          isLandscape={isLandscape}
          motionProps={{
            initial: { opacity: 0, y: -10 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.4 },
            style: { marginBottom: shouldRotate || window.innerWidth >= 640 ? '1.5rem' : '0.75rem' },
          }}
        />

        {/* Game Logo */}
        <ThemeLogo
          kind='game'
          theme={theme}
          isLandscape={isLandscape}
          motionProps={{
            initial: { opacity: 0, scale: 0.95 },
            animate: { opacity: 1, scale: 1 },
            transition: { duration: 0.5, delay: 0.1 },
            style: { marginBottom: shouldRotate || window.innerWidth >= 640 ? '2rem' : '1rem' },
          }}
        />

        {/* Loading Bar - Hidden during lobby/matchmaking phases, shown for other loading phases */}
        {phase !== 'page_viewed' && phase !== 'environment_check' && phase !== 'creating_lobby' && phase !== 'matchmaking' && phase !== 'match_found' && phase !== 'joining_broadcast' && phase !== 'preparing_player' && (
          <motion.div
            className='w-full'
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <LoadingBar theme={theme} height={getResponsiveHeight(theme.loadingBarHeight || 24, undefined, isLandscape)} />
          </motion.div>
        )}

        </div>
      </div>
      </div>
      <ScreenEdgeBorder animationDuration={theme.animatedWishlistButtonSpeed || 1000} />
    </>
  );
};

/**
 * Minimal loading overlay - just the message and loading bar
 * Use this for phases where you don't want the full branded experience
 */
export const MinimalLoadingOverlay = ({
  theme,
  message,
  showLoadingBar = true,
}: {
  theme: QuickstartTheme;
  message?: string;
  showLoadingBar?: boolean;
}) => {
  const displayMessage = message ?? getLoadingMessage(theme, 'initial');

  // Detect landscape orientation (forced on mobile)
  const isLandscape = useIsLandscape();

  // Detect actual physical orientation for rotation
  const isPortraitOrientation = useIsPortraitOrientation();

  // Apply rotation on mobile devices in portrait orientation
  const shouldRotate = isLandscape && isPortraitOrientation;

  return (
    <div
      className='pointer-events-none fixed z-10 flex flex-col items-center justify-center'
      style={
        shouldRotate ? {
          // Portrait mode: swap dimensions and rotate
          position: 'fixed',
          left: '50%',
          top: '50%',
          width: '100dvh',
          height: '100dvw',
          transform: 'translate(-50%, -50%) rotate(var(--force-rotate-angle, 90deg))',
          transformOrigin: 'center center',
        } : {
          // Normal mode: full screen
          inset: 0,
        }
      }
    >
      <div className='flex w-full flex-col items-center'>
        <div className='flex w-full max-w-md flex-col items-center px-6 text-center'>
        {/* Loading Bar */}
        {showLoadingBar && (
          <div className='w-full'>
            <LoadingBar theme={theme} height={getResponsiveHeight(theme.loadingBarHeight || 24, undefined, isLandscape)} />
          </div>
        )}

        {/* Loading Message */}
        <p
          className='mb-6 max-w-md font-medium'
          style={{
            color: theme.badgeOverrideTextColor ?? '#FEF3C7',
            fontSize: shouldRotate || window.innerWidth >= 768 ? '1.25rem' : '1.125rem',
          }}
        >
          {displayMessage}
        </p>
        </div>
      </div>
    </div>
  );
};

export default SeamlessLoadingOverlay;

